/**
 * Elegir la cuenta del catálogo del contador para un concepto («Gasto por depreciación»…).
 *
 * 🔴 POR QUÉ NO BASTA UN DESPLEGABLE CON LAS SUGERIDAS (auditoría del 29-oct-2026). El desplegable
 * ofrecía solo los 8 `candidatos` del emparejador. Si la cuenta buena no estaba entre ellos, el
 * contador no tenía cómo elegirla. El auditor terminó vinculando la depreciación «a cualquier
 * cuenta», y eso manda las partidas de depreciación a donde no van.
 *
 * Aquí las sugeridas van arriba, y debajo TODAS las cuentas imputables de los tipos que admite el
 * concepto, con búsqueda por código o nombre. El tipo sigue sin cruzarse: es la misma regla del
 * emparejador.
 */
import { useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ChevronDown, Search } from 'lucide-react'

/** Cuántas filas de «todas» se pintan a la vez. Con búsqueda, nadie recorre 500 a mano. */
const TOPE_VISIBLES = 200

const normalizar = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')

/** Las imputables del plan que el concepto admite, sin las que ya van como sugeridas. */
export function cuentasDelConcepto(plan, tipos, candidatos) {
  const sugeridas = new Set((candidatos || []).map((c) => c.codigo))
  const admitidos = new Set(tipos || [])
  return (plan || []).filter(
    (c) =>
      c.permiteMovimiento !== 0 &&
      !sugeridas.has(c.codigo) &&
      (admitidos.size === 0 || !c.tipo || admitidos.has(c.tipo)),
  )
}

export function filtrarCuentas(cuentas, busqueda) {
  const q = normalizar(busqueda).trim()
  if (!q) return cuentas
  const palabras = q.split(/\s+/)
  return cuentas.filter((c) => {
    const texto = `${c.codigo} ${normalizar(c.nombre)}`
    return palabras.every((p) => texto.includes(p))
  })
}

export default function ElegirCuentaCatalogo({ etiqueta, valor, candidatos, plan, tipos, onElegir }) {
  const [abierto, setAbierto] = useState(false)
  const [busqueda, setBusqueda] = useState('')

  const todas = useMemo(() => cuentasDelConcepto(plan, tipos, candidatos), [plan, tipos, candidatos])
  const sugeridasVisibles = useMemo(() => filtrarCuentas(candidatos || [], busqueda), [candidatos, busqueda])
  const todasVisibles = useMemo(() => filtrarCuentas(todas, busqueda), [todas, busqueda])

  const elegida = useMemo(() => {
    if (!valor) return null
    return (candidatos || []).find((c) => c.codigo === valor) || (plan || []).find((c) => c.codigo === valor) || { codigo: valor, nombre: '' }
  }, [valor, candidatos, plan])

  const elegir = (codigo) => {
    onElegir(codigo)
    setAbierto(false)
    setBusqueda('')
  }

  const fila = (c) => (
    <li key={c.codigo}>
      <button
        type="button"
        onClick={() => elegir(c.codigo)}
        className={`w-full text-left px-3 py-2 rounded-md text-body-sm hover:bg-neutral-100 dark:hover:bg-neutral-800 ${
          c.codigo === valor ? 'bg-neutral-100 dark:bg-neutral-800 font-medium' : ''
        }`}
      >
        <span className="tabular-nums text-neutral-500 dark:text-neutral-400 mr-2">{c.codigo}</span>
        <span className="text-neutral-900 dark:text-neutral-50">{c.nombre}</span>
      </button>
    </li>
  )

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="w-56 sm:w-80 max-w-full h-10 sm:h-9 justify-between font-normal"
        onClick={() => setAbierto(true)}
        aria-label={`Elegir la cuenta para ${etiqueta}`}
      >
        <span className="truncate">{elegida ? `${elegida.codigo}${elegida.nombre ? ` — ${elegida.nombre}` : ''}` : 'Sin elegir'}</span>
        <ChevronDown size={16} className="ml-2 shrink-0 opacity-60" />
      </Button>

      <Dialog open={abierto} onOpenChange={(v) => { setAbierto(v); if (!v) setBusqueda('') }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{etiqueta}</DialogTitle>
            <DialogDescription>
              Elige la cuenta de tu catálogo. Arriba van las que el sistema sugiere; debajo, todas las del tipo que corresponde.
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              autoFocus
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por código o nombre (p. ej. depreciación)"
              className="pl-9"
            />
          </div>

          <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1 space-y-3">
            {sugeridasVisibles.length > 0 && (
              <section>
                <p className="text-caption uppercase tracking-wide text-neutral-500 dark:text-neutral-400 px-3 mb-1">Sugeridas</p>
                <ul>{sugeridasVisibles.map(fila)}</ul>
              </section>
            )}
            <section>
              <p className="text-caption uppercase tracking-wide text-neutral-500 dark:text-neutral-400 px-3 mb-1">
                Todas las cuentas del tipo ({todasVisibles.length})
              </p>
              {todasVisibles.length === 0 ? (
                <p className="px-3 py-2 text-body-sm text-neutral-500 dark:text-neutral-400">
                  Ninguna cuenta del tipo coincide con la búsqueda.
                </p>
              ) : (
                <ul>{todasVisibles.slice(0, TOPE_VISIBLES).map(fila)}</ul>
              )}
              {todasVisibles.length > TOPE_VISIBLES && (
                <p className="px-3 py-2 text-caption text-neutral-500 dark:text-neutral-400">
                  Se muestran {TOPE_VISIBLES} de {todasVisibles.length}. Escribe para acotar.
                </p>
              )}
            </section>
          </div>

          {valor && (
            <div className="flex justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={() => elegir(null)}>
                Quitar la cuenta elegida
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
